import { useState, useCallback } from "react";
import {
  GetDeliveryPartners,
  GetDeliveryStats,
  GetStorePreference,
  UpdateStorePreference,
  SyncIntegratedOrders,
  GetMerchantDashboard,
  RetryOrderSync,
  GetSettlementSummary,
  GetSettlementLedger,
} from "../../Api";
import {
  DeliveryPartner,
  DeliveryStats,
  IntegratedOrder,
  StorePreference,
  SettlementSummary,
  SettlementTransaction,
} from "./types";

export const useDeliveryPartners = () => {
  const [partnersList, setPartnersList] = useState<DeliveryPartner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState<boolean>(false);

  const fetchPartners = useCallback(async () => {
    try {
      setLoadingPartners(true);
      const res = await GetDeliveryPartners();
      if (res?.status && Array.isArray(res?.data)) {
        setPartnersList(res.data);
      }
      return res?.data;
    } catch (error) {
      console.error("Error fetching delivery partners catalog", error);
    } finally {
      setLoadingPartners(false);
    }
  }, []);

  return { partnersList, loadingPartners, fetchPartners };
};

export const useDeliveryStats = () => {
  const [stats, setStats] = useState<DeliveryStats | null>(null);
  const [loadingStats, setLoadingStats] = useState<boolean>(false);

  const fetchStats = useCallback(async (dateRange: string = "") => {
    try {
      setLoadingStats(true);
      const res = await GetDeliveryStats(dateRange);
      if (res?.status && res?.data) {
        setStats(res.data);
      }
      return res?.data;
    } catch (error) {
      console.error("Error fetching delivery stats", error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  return { stats, loadingStats, fetchStats };
};

export const useSettlementSummary = () => {
  const [settlementSummary, setSettlementSummary] = useState<SettlementSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);

  const fetchSettlementSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const res = await GetSettlementSummary();
      if (res?.status && res?.data) {
        setSettlementSummary(res.data);
      }
      return res?.data;
    } catch (error) {
      console.error("Error fetching settlement summary", error);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  return { settlementSummary, loadingSummary, fetchSettlementSummary };
};

export const useSettlementLedger = () => {
  const [transactions, setTransactions] = useState<SettlementTransaction[]>([]);
  const [loadingLedger, setLoadingLedger] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const fetchSettlementLedger = useCallback(
    async (page: number = 1, pageSize: number = 20, eventType: string = "") => {
      try {
        setLoadingLedger(true);
        const res = await GetSettlementLedger(page, pageSize, eventType);
        if (res?.status && res?.data) {
          const ledgerData = res.data;
          setTransactions(ledgerData.results || []);
          const count = ledgerData.count || (ledgerData.results ? ledgerData.results.length : 0);
          setTotalCount(count);
          setTotalPages(Math.ceil(count / pageSize));
        } else {
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(0);
        }
        return res;
      } catch (error) {
        console.error("Error fetching settlement ledger", error);
        setTransactions([]);
        setTotalCount(0);
        setTotalPages(0);
      } finally {
        setLoadingLedger(false);
      }
    },
    []
  );

  return { transactions, loadingLedger, totalCount, totalPages, fetchSettlementLedger };
};

export const useStorePreference = () => {
  const [preference, setPreference] = useState<StorePreference | null>(null);
  const [loadingPreference, setLoadingPreference] = useState<boolean>(false);

  const fetchPreference = useCallback(async () => {
    try {
      setLoadingPreference(true);
      const res = await GetStorePreference();
      if (res?.status && res?.data) {
        setPreference(res.data);
      }
      return res?.data;
    } catch (error) {
      console.error("Error fetching store preference", error);
    } finally {
      setLoadingPreference(false);
    }
  }, []);

  const updatePreference = useCallback(async (isEnabled: boolean) => {
    try {
      setLoadingPreference(true);
      const res = await UpdateStorePreference(isEnabled);
      if (res?.status && res?.data) {
        setPreference(res.data);
      }
      return res;
    } catch (error) {
      console.error("Error updating store preference", error);
      throw error;
    } finally {
      setLoadingPreference(false);
    }
  }, []);

  return { preference, loadingPreference, fetchPreference, updatePreference };
};

export const useIntegratedOrderList = () => {
  const [orders, setOrders] = useState<IntegratedOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const fetchIntegratedOrders = useCallback(
    async (page: number = 1, size: number = 20, status: string = "", dateRange: string = "") => {
      try {
        setLoading(true);
        const res = await GetMerchantDashboard(page, size, status, dateRange);
        if (res?.results) {
          setOrders(res.results);
          const count = res.count || res.results.length || 0;
          setTotalCount(count);
          setPageSize(size);
          setTotalPages(Math.ceil(count / size));
        } else {
          setOrders([]);
          setTotalCount(0);
          setTotalPages(0);
        }
        return res;
      } catch (error) {
        console.error("Error fetching integrated orders", error);
        setOrders([]);
        setTotalCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    orders,
    loading,
    totalCount,
    totalPages,
    pageSize,
    fetchIntegratedOrders,
    setOrders,
    setTotalPages,
  };
};

export const useSyncOrders = () => {
  const [syncing, setSyncing] = useState<boolean>(false);

  const syncOrders = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await SyncIntegratedOrders();
      return res;
    } catch (error) {
      console.error("Error syncing integrated orders", error);
      throw error;
    } finally {
      setSyncing(false);
    }
  }, []);

  return { syncing, syncOrders };
};

export const useRetryOrderSync = () => {
  const [retrying, setRetrying] = useState<boolean>(false);

  const retrySync = useCallback(async (orderId: number) => {
    try {
      setRetrying(true);
      const res = await RetryOrderSync(orderId);
      return res;
    } catch (error) {
      console.error("Error retrying order sync", error);
      throw error;
    } finally {
      setRetrying(false);
    }
  }, []);

  return { retrying, retrySync };
};
